import { i18n } from '$lib/i18n/i18n.svelte';

/** Dates are handled as local "YYYY-MM-DD" keys, matching DateOnly on the API. */
export type DateKey = string;

const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;
const DAYS_PER_YEAR = 365;
const MS_PER_DAY = 86_400_000;

export function toDateKey(date: Date): DateKey {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${String(date.getFullYear()).padStart(4, '0')}-${month}-${day}`;
}

export function parseDateKey(key: DateKey): Date {
	const [year, month, day] = key.split('-').map(Number);
	const date = new Date(2000, month - 1, day);
	// Set the year apart: Date reads years below 100 as 19xx (old weekly chores are anchored in year 1).
	date.setFullYear(year);
	return date;
}

export function todayKey(): DateKey {
	return toDateKey(new Date());
}

export function addDays(key: DateKey, days: number): DateKey {
	const date = parseDateKey(key);
	date.setDate(date.getDate() + days);
	return toDateKey(date);
}

/** Whole days from a to b (negative if b is before a). */
export function daysBetween(a: DateKey, b: DateKey): number {
	return Math.round((parseDateKey(b).getTime() - parseDateKey(a).getTime()) / MS_PER_DAY);
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndex(key: DateKey): number {
	return (parseDateKey(key).getDay() + 6) % 7;
}

export function mondayOf(key: DateKey): DateKey {
	return addDays(key, -weekdayIndex(key));
}

export function weekDays(monday: DateKey): DateKey[] {
	return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

export function isoWeekNumber(key: DateKey): number {
	const date = parseDateKey(key);
	const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
	const day = utc.getUTCDay() || 7;
	utc.setUTCDate(utc.getUTCDate() + 4 - day);
	const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
	return Math.ceil(((utc.getTime() - yearStart.getTime()) / MS_PER_DAY + 1) / 7);
}

/** "22. sep" */
export function formatDayMonth(key: DateKey): string {
	const date = parseDateKey(key);
	return i18n.t.dates.dayMonth(date.getDate(), i18n.t.dates.months[date.getMonth()]);
}

/** When something happened, from a timestamp: "i dag", "i går", else "2. okt". */
export function dayText(timestamp: string, today: DateKey = todayKey()): string {
	const key = toDateKey(new Date(timestamp));
	const days = daysBetween(key, today);
	if (days === 0) return i18n.t.dates.today.toLocaleLowerCase(i18n.locale);
	if (days === 1) return i18n.t.dates.yesterday.toLocaleLowerCase(i18n.locale);
	return formatDayMonth(key);
}

/** "30. okt.", or with the weekday "tor. 30. okt.", or that without the month "tor. 30." */
export function formatShortDate(
	key: DateKey,
	{ weekday = false, month = true }: { weekday?: boolean; month?: boolean } = {}
): string {
	const t = i18n.t.dates;
	const date = parseDateKey(key);
	return t.shortDate(
		weekday ? t.weekdaysAbbr[weekdayIndex(key)] : null,
		date.getDate(),
		month ? t.monthsAbbr[date.getMonth()] : null
	);
}

/** "30. oktober" */
export function formatLongDate(key: DateKey): string {
	const date = parseDateKey(key);
	return i18n.t.dates.dayMonth(date.getDate(), i18n.t.dates.monthsLong[date.getMonth()]);
}

/** "Mandag 22. sep" */
export function formatLongDay(key: DateKey): string {
	return i18n.t.dates.longDay(i18n.t.dates.weekdays[weekdayIndex(key)], formatDayMonth(key));
}

/** "Denne uge", "Næste uge", "Om 3 uger", "2 uger siden" … */
export function weekRelativeText(offset: number): string {
	const t = i18n.t.dates;
	if (offset === 0) return t.thisWeek;
	if (offset === 1) return t.nextWeek;
	if (offset === -1) return t.lastWeek;
	return offset > 0 ? t.inWeeks(offset) : t.weeksAgo(-offset);
}

/** "Uge 39 · 22. sep – 28. sep" */
export function weekLabel(monday: DateKey): string {
	return i18n.t.dates.weekLabel(
		isoWeekNumber(monday),
		formatDayMonth(monday),
		formatDayMonth(addDays(monday, 6))
	);
}

/** How long ago a recipe was last cooked: "I dag", "I går", "3 dage siden", "2 uger siden" … */
export function agoText(key: DateKey, today: DateKey): string {
	const t = i18n.t.dates;
	const days = daysBetween(key, today);
	if (days <= 0) return t.today;
	if (days === 1) return t.yesterday;
	if (days < DAYS_PER_WEEK) return t.daysAgo(days);
	if (days < DAYS_PER_MONTH) return t.weeksAgo(Math.floor(days / DAYS_PER_WEEK));
	if (days < DAYS_PER_YEAR) return t.monthsAgo(Math.floor(days / DAYS_PER_MONTH));
	return t.overAYearAgo;
}

/** "Lagt ind i dag", "Lagt ind for 12 dage siden", "Lagt ind for 3 mdr. siden" … */
export function addedAgoText(days: number): string {
	const t = i18n.t.dates;
	if (days <= 0) return t.addedToday;
	if (days === 1) return t.addedYesterday;
	if (days < 2 * DAYS_PER_WEEK) return t.addedDaysAgo(days);
	if (days < 2 * DAYS_PER_MONTH) return t.addedWeeksAgo(Math.floor(days / DAYS_PER_WEEK));
	return t.addedMonthsAgo(Math.floor(days / DAYS_PER_MONTH));
}

/** A shelf life in its largest whole unit: 3 → "3 dage", 14 → "2 uger", 90 → "3 mdr.". */
export function formatDuration(days: number): string {
	const t = i18n.t.dates.duration;
	if (days % DAYS_PER_YEAR === 0) return t.years(days / DAYS_PER_YEAR);
	if (days % DAYS_PER_MONTH === 0) return t.months(days / DAYS_PER_MONTH);
	if (days % DAYS_PER_WEEK === 0) return t.weeks(days / DAYS_PER_WEEK);
	return t.days(days);
}
